import React, { useState, useMemo, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { recruitmentTheme, themeClasses, getStatusColor } from '../styles/theme';
import { StatusBadge, taskStatusOptions, priorityOptions } from '../components/ui/StatusBadge';
import { enhancedSearch } from '../utils/searchUtils';
import { subscribeToTasks, addTask as addTaskToFirebase, updateTask as updateTaskInFirebase, deleteTask as deleteTaskFromFirebase, Task as FirebaseTask } from '../services/taskService';
import { addToRecycleBin } from '../utils/recycleBin';
import { useNotifications } from '../contexts/NotificationContext';
import { 
  CheckSquare, 
  Clock, 
  AlertCircle, 
  Plus, 
  Search, 
  Filter,
  Calendar,
  User,
  Target,
  CheckCircle,
  X,
  Save,
  ArrowUpDown,
  Trash2,
  Edit,
  Archive,
  GripVertical,
  Settings
} from 'lucide-react';

// Use Firebase Task interface
type Task = FirebaseTask;

type ColumnConfig = {
  [key: string]: {
    title: string;
    width: string;
    sortable: boolean;
    sortField?: keyof Task;
  };
};

const columnConfig: ColumnConfig = {
  select: { title: '', width: 'w-12', sortable: false },
  task: { title: 'Task', width: '', sortable: true, sortField: 'title' },
  assignedTo: { title: 'Assigned To', width: '', sortable: true, sortField: 'assignedTo' },
  dueDate: { title: 'Due Date', width: '', sortable: true, sortField: 'dueDate' },
  priority: { title: 'Priority', width: '', sortable: true, sortField: 'priority' },
  category: { title: 'Category', width: '', sortable: true, sortField: 'category' },
  status: { title: 'Status', width: '', sortable: false },
};

const TasksPage: React.FC = () => {
  const { notifyBadgeChange, notifyContentChange, showNotification } = useNotifications();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [selectedTasks, setSelectedTasks] = useState<string[]>([]);
  const [columnOrder, setColumnOrder] = useState([
    'select', 'task', 'assignedTo', 'dueDate', 'priority', 'category', 'status'
  ]);
  const [draggedColumn, setDraggedColumn] = useState<string | null>(null);
  const [showAutoDeletionSettings, setShowAutoDeletionSettings] = useState(false);
  const [autoDeletionEnabled, setAutoDeletionEnabled] = useState(false);
  const [autoDeletionDays, setAutoDeletionDays] = useState(30);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    assignedTo: '',
    dueDate: '',
    priority: 'MEDIUM',
    category: ''
  });

  const statusOptions = ['PENDING', 'IN_PROGRESS', 'COMPLETED', 'OVERDUE'];
  const localPriorityOptions = ['LOW', 'MEDIUM', 'HIGH'];
  const categoryOptions = ['Follow-up', 'Screening', 'Interview', 'Data Entry', 'Medical', 'Documentation'];

  // Subscribe to real-time tasks updates from Firebase
  useEffect(() => {
    console.log('📡 Setting up Firebase real-time listener for tasks...');
    console.log('🔍 Current tasks state before Firebase listener:', tasks.length);
    
    let unsubscribe: (() => void) | null = null;
    
    const setupSubscription = async () => {
      try {
        unsubscribe = await subscribeToTasks((firebaseTasks) => {
          console.log('🔄 Received', firebaseTasks.length, 'tasks from Firebase');
          console.log('📋 Firebase tasks data:', firebaseTasks);
          setTasks(firebaseTasks);
        }, (error) => {
          console.error('❌ Firebase subscription error:', error);
          console.error('❌ Error details:', error.message, error.code);
          // Show user-friendly error message
          alert(`Firebase connection error: ${error.message}. Tasks may not persist.`);
        });
      } catch (error) {
        console.error('❌ Failed to setup Firebase subscription:', error);
        alert('Failed to connect to Firebase. Tasks will not persist.');
      }
    };
    
    setupSubscription();
    
    // Cleanup subscription on unmount
    return () => {
      console.log('🔌 Cleaning up Firebase tasks subscription...');
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  const handleSort = (field: string) => {
    const newDirection = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(newDirection);
  };

  const handleSelectTask = (taskId: string, isSelected: boolean) => {
    if (isSelected) {
      setSelectedTasks(prev => [...prev, taskId]);
    } else {
      setSelectedTasks(prev => prev.filter(id => id !== taskId));
    }
  };

  const handleSelectAll = (isSelected: boolean) => {
    if (isSelected) {
      setSelectedTasks(filteredTasks.map(task => task.id || '').filter(Boolean));
    } else {
      setSelectedTasks([]);
    }
  };

  const handleDeleteSelected = async () => {
    if (window.confirm(`Are you sure you want to delete ${selectedTasks.length} task(s)? They will be moved to the recycle bin.`)) {
      // Get task names before deletion for notifications
      const tasksToDelete = tasks.filter(t => t.id && selectedTasks.includes(t.id));
      console.log('🗑️ Bulk deleting tasks:', tasksToDelete.map(t => ({ id: t.id, title: t.title })));
      console.log('📋 Tasks before bulk deletion:', tasks.length);
      
      let deletedCount = 0;
      let errorCount = 0;
      
      // Delete tasks from Firebase in parallel
      const deletePromises = selectedTasks.map(async (taskId) => {
        const taskToDelete = tasks.find(t => t.id === taskId);
        
        if (taskToDelete) {
          try {
            await deleteTaskFromFirebase(taskId);
            console.log(`🔥 Successfully deleted task ${taskId} from Firebase`);
            
            // Add to recycle bin
            addToRecycleBin({
              type: 'task',
              name: taskToDelete.title,
              details: `Priority: ${taskToDelete.priority} | Due: ${taskToDelete.dueDate || 'No due date'} | Assigned to: ${taskToDelete.assignedTo}`,
              deletedBy: 'Current User',
              data: taskToDelete
            });
            
            deletedCount++;
            // Notify content change
            notifyContentChange('Tasks Page', 'deleted', 'Task', taskToDelete.title);
          } catch (error) {
            errorCount++;
            console.error(`❌ Error deleting task ${taskId} from Firebase:`, error);
          }
        } else {
          errorCount++;
          console.error(`❌ Task ${taskId} not found in current tasks`);
        }
      });
      
      // Wait for all deletions to complete
      await Promise.all(deletePromises);
      setSelectedTasks([]);
      
      // Show appropriate feedback message
      if (errorCount === 0) {
        alert(`${deletedCount} task(s) successfully moved to recycle bin!`);
      } else if (deletedCount > 0) {
        alert(`${deletedCount} task(s) moved to recycle bin. ${errorCount} task(s) failed to delete.`);
      } else {
        alert(`Failed to delete ${errorCount} task(s). Please try again.`);
      }
    }
  };

  const handleArchiveCompleted = () => {
    const completedTasks = tasks.filter(task => task.status === 'COMPLETED');
    if (completedTasks.length === 0) {
      alert('No completed tasks to archive.');
      return;
    }
    
    if (window.confirm(`Archive ${completedTasks.length} completed task(s)?`)) {
      setTasks(prev => prev.filter(task => task.status !== 'COMPLETED'));
      alert(`${completedTasks.length} completed tasks have been archived.`);
    }
  };

  // Filter and sort tasks
  const filteredTasks = useMemo(() => {
    let filtered = tasks;

    if (searchTerm) {
      filtered = filtered.filter(task => 
        enhancedSearch(searchTerm, [
          task.title,
          task.description,
          task.assignedTo,
          task.assignedBy,
          task.category,
          task.dueDate,
          task.status,
          task.priority,
          task.id
        ])
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(task => task.status === statusFilter);
    }

    if (priorityFilter !== 'all') {
      filtered = filtered.filter(task => task.priority === priorityFilter);
    }

    // Apply sorting
    if (sortField) {
      filtered = [...filtered].sort((a, b) => {
        const aValue = a[sortField as keyof Task];
        const bValue = b[sortField as keyof Task];

        // Handle null or undefined values to avoid errors, placing them at the end.
        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return 1;
        if (bValue == null) return -1;

        const comparableA = typeof aValue === 'string' ? aValue.toLowerCase() : aValue;
        const comparableB = typeof bValue === 'string' ? bValue.toLowerCase() : bValue;

        let comparison = 0;
        if (comparableA > comparableB) {
          comparison = 1;
        } else if (comparableA < comparableB) {
          comparison = -1;
        }

        return sortDirection === 'desc' ? -comparison : comparison;
      });
    }

    return filtered;
  }, [tasks, searchTerm, statusFilter, priorityFilter, sortField, sortDirection]);

  const updateTaskStatus = (taskId: string, newStatus: string) => {
    const mappedStatus = newStatus.toUpperCase().replace('-', '_') as Task['status'];
    const currentTask = tasks.find(t => t.id === taskId);
    const oldStatus = currentTask?.status;
    
    const updatedTasks = tasks.map(task => 
      task.id === taskId ? { ...task, status: mappedStatus } : task
    );
    setTasks(updatedTasks);
    
    // Update Firebase
    updateTaskInFirebase(taskId, { status: mappedStatus }).catch(error => {
      console.error('❌ Failed to update task status in Firebase:', error);
      // Revert local state on error
      setTasks(tasks);
    });
    
    // Notify badge change
    if (oldStatus && oldStatus !== mappedStatus) {
      notifyBadgeChange('Tasks Page', 'Task Status', oldStatus, mappedStatus);
    }
  };

  const updateTaskPriority = (taskId: string, newPriority: string) => {
    const mappedPriority = newPriority.toUpperCase() as Task['priority'];
    const currentTask = tasks.find(t => t.id === taskId);
    const oldPriority = currentTask?.priority;
    
    const updatedTasks = tasks.map(task => 
      task.id === taskId ? { ...task, priority: mappedPriority } : task
    );
    setTasks(updatedTasks);
    
    // Update Firebase
    updateTaskInFirebase(taskId, { priority: mappedPriority }).catch(error => {
      console.error('❌ Failed to update task priority in Firebase:', error);
      // Revert local state on error
      setTasks(tasks);
    });
    
    // Notify badge change
    if (oldPriority && oldPriority !== mappedPriority) {
      notifyBadgeChange('Tasks Page', 'Task Priority', oldPriority, mappedPriority);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'IN_PROGRESS':
        return <Clock className="w-4 h-4 text-blue-600" />;
      case 'OVERDUE':
        return <AlertCircle className="w-4 h-4 text-red-600" />;
      case 'PENDING':
        return <Target className="w-4 h-4 text-yellow-600" />;
      default:
        return <CheckSquare className="w-4 h-4 text-gray-600" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'HIGH':
        return 'bg-red-100 text-red-800';
      case 'MEDIUM':
        return 'bg-yellow-100 text-yellow-800';
      case 'LOW':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusSelectColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'IN_PROGRESS':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'OVERDUE':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'PENDING':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      assignedTo: '',
      dueDate: '',
      priority: 'MEDIUM',
      category: ''
    });
    setEditingTaskId(null);
  };

  const handleDragStart = (e: React.DragEvent, columnId: string) => {
    setDraggedColumn(columnId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, targetColumnId: string) => {
    e.preventDefault();
    if (!draggedColumn || draggedColumn === targetColumnId) return;

    const newOrder = [...columnOrder];
    const draggedIndex = newOrder.indexOf(draggedColumn);
    const targetIndex = newOrder.indexOf(targetColumnId);

    newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, draggedColumn);

    setColumnOrder(newOrder);
    setDraggedColumn(null);
  };

  const handleAutoDeletionToggle = () => {
    setAutoDeletionEnabled(!autoDeletionEnabled);
    if (!autoDeletionEnabled) {
      alert(`Auto-deletion enabled: Completed tasks will be deleted after ${autoDeletionDays} days`);
    } else {
      alert('Auto-deletion disabled');
    }
  };

  const getCompletedTasksForDeletion = () => {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - autoDeletionDays);
    
    return tasks.filter(task => 
      task.status === 'COMPLETED' && 
      new Date(task.dueDate) < cutoffDate
    );
  };

  const executeTaskAutoDeletion = async () => {
    const tasksToDelete = getCompletedTasksForDeletion();
    
    if (tasksToDelete.length === 0) {
      alert('No tasks eligible for auto-deletion at this time.');
      return;
    }

    if (window.confirm(
      `Auto-delete ${tasksToDelete.length} completed task(s) that are older than ${autoDeletionDays} days? They will be moved to the recycle bin.\n\n` +
      tasksToDelete.map(t => `• ${t.title} (${t.status})`).join('\n')
    )) {
      // Delete from Firebase
      let deletedCount = 0;
      const deletePromises = tasksToDelete.map(async (task) => {
        try {
          await deleteTaskFromFirebase(task.id!);
          
          // Add to recycle bin
          addToRecycleBin({
            type: 'task',
            name: task.title,
            details: `Priority: ${task.priority} | Due: ${task.dueDate || 'No due date'} | Assigned to: ${task.assignedTo}`,
            deletedBy: 'Auto-deletion',
            data: task
          });
          
          deletedCount++;
        } catch (error) {
          console.error('❌ Failed to delete task in auto-deletion:', error);
        }
      });
      
      await Promise.all(deletePromises);
      alert(`${deletedCount} completed task(s) moved to recycle bin!`);
    }
  };

  const renderTaskCell = (task: Task, columnId: string) => {
    switch (columnId) {
      case 'select':
        return (
          <input
            type="checkbox"
            checked={task.id ? selectedTasks.includes(task.id) : false}
            onChange={(e) => task.id && handleSelectTask(task.id, e.target.checked)}
            className="rounded"
          />
        );
      case 'task':
        return (
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="font-medium text-[#8b6914]">{task.title}</div>
              <div className="text-sm text-gray-500">{task.description}</div>
            </div>
            <div className="flex items-center space-x-1 ml-2">
              <button
                onClick={() => handleEditTask(task)}
                className={`${recruitmentTheme.components.button.ghost} text-blue-600 hover:text-blue-700 p-1`}
                title="Edit task"
              >
                <Edit className="w-3 h-3" />
              </button>
              <button
                onClick={() => task.id && handleDeleteTask(task.id)}
                className={`${recruitmentTheme.components.button.ghost} text-red-600 hover:text-red-700 p-1`}
                title="Delete task"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          </div>
        );
      case 'assignedTo':
        return (
          <div>
            <div className="flex items-center space-x-2">
              <User className="w-4 h-4 text-gray-400" />
              <span className="text-gray-600">{task.assignedTo}</span>
            </div>
            <div className="text-xs text-gray-400">by {task.assignedBy}</div>
          </div>
        );
      case 'dueDate':
        return (
          <div className="flex items-center space-x-2">
            <Calendar className="w-4 h-4 text-gray-400" />
            <span className="text-gray-600">{task.dueDate}</span>
          </div>
        );
      case 'priority':
        return (
          <StatusBadge
            currentStatus={task.priority.toLowerCase()}
            options={priorityOptions}
            onStatusChange={(newPriority) => task.id && updateTaskPriority(task.id, newPriority)}
            size="sm"
          />
        );
      case 'category':
        return (
          <StatusBadge
            currentStatus={task.category.toLowerCase().replace(/\s+/g, '-')}
            options={[
              { value: 'follow-up', label: 'Follow-up', color: 'text-blue-700', bgColor: 'bg-blue-100' },
              { value: 'screening', label: 'Screening', color: 'text-green-700', bgColor: 'bg-green-100' },
              { value: 'interview', label: 'Interview', color: 'text-purple-700', bgColor: 'bg-purple-100' },
              { value: 'data-entry', label: 'Data Entry', color: 'text-gray-700', bgColor: 'bg-gray-100' },
              { value: 'medical', label: 'Medical', color: 'text-red-700', bgColor: 'bg-red-100' },
              { value: 'documentation', label: 'Documentation', color: 'text-orange-700', bgColor: 'bg-orange-100' }
            ]}
            onStatusChange={(newCategory) => {
              const formattedCategory = newCategory.split('-').map(word => 
                word.charAt(0).toUpperCase() + word.slice(1)
              ).join(' ');
              const oldCategory = task.category;
              
              const updatedTasks = tasks.map(t => t.id === task.id ? { ...t, category: formattedCategory } : t);
              setTasks(updatedTasks);
              
              // Update Firebase
              task.id && updateTaskInFirebase(task.id, { category: formattedCategory }).catch(error => {
                console.error('❌ Failed to update task category in Firebase:', error);
                // Revert local state on error
                setTasks(tasks);
              });
              
              // Notify badge change
              if (oldCategory !== formattedCategory) {
                notifyBadgeChange('Tasks Page', 'Task Category', oldCategory, formattedCategory);
              }
            }}
            size="sm"
          />
        );
      case 'status':
        return (
          <StatusBadge
            currentStatus={task.status.toLowerCase().replace('_', '-')}
            options={taskStatusOptions}
            onStatusChange={(newStatus) => task.id && updateTaskStatus(task.id, newStatus)}
            size="sm"
          />
        );
      default:
        return null;
    }
  };

  const handleAddTask = async () => {
    if (editingTaskId) {
      // Edit existing task
      const updatedTaskData = {
        title: formData.title,
        description: formData.description,
        assignedTo: formData.assignedTo,
        dueDate: formData.dueDate,
        priority: formData.priority as Task['priority'],
        category: formData.category
      };

      try {
        // Update using Firebase
        await updateTaskInFirebase(editingTaskId, updatedTaskData);
        
        // Show success notification for task update
        showNotification({
          type: 'success',
          title: 'Task Updated',
          message: `Task "${formData.title}" has been updated successfully`,
          priority: 'medium'
        });
        
        alert('Task updated successfully!');
      } catch (error) {
        console.error('❌ Failed to update task in Firebase:', error);
        alert('Failed to update task.');
      }
    } else {
      // Add new task
      const taskData = {
        title: formData.title,
        description: formData.description,
        assignedTo: formData.assignedTo,
        assignedBy: 'Admin', // In a real app, this would be the current user
        dueDate: formData.dueDate,
        status: 'PENDING' as FirebaseTask['status'],
        priority: formData.priority as FirebaseTask['priority'],
        category: formData.category
      };

      try {
        // Add to Firebase
        await addTaskToFirebase(taskData);
        
        // Notify content change
        notifyContentChange('Tasks Page', 'added', 'Task', formData.title);
        
        alert('Task created successfully!');
      } catch (error) {
        console.error('❌ Failed to add task to Firebase:', error);
        alert('Failed to create task.');
      }
    }
    
    setShowAddModal(false);
    setEditingTaskId(null);
    resetForm();
  };

  const handleDeleteTask = async (taskId: string) => {
    if (!window.confirm('Are you sure you want to delete this task? It will be moved to the recycle bin.')) {
      return;
    }
    
    const taskToDelete = tasks.find(t => t.id === taskId);
    console.log('🗑️ Attempting to delete task:', taskToDelete);
    console.log('📋 Tasks before deletion:', tasks.length);
    
    if (!taskToDelete) {
      console.error(`❌ Task ${taskId} not found in current tasks`);
      alert('Task not found. Please refresh the page and try again.');
      return;
    }
    
    try {
      // Delete from Firebase
      await deleteTaskFromFirebase(taskId);
      console.log('🔥 Deleted task from Firebase:', taskId);
      
      // Add to recycle bin
      addToRecycleBin({
        type: 'task',
        name: taskToDelete.title,
        details: `Priority: ${taskToDelete.priority} | Due: ${taskToDelete.dueDate || 'No due date'} | Assigned to: ${taskToDelete.assignedTo}`,
        deletedBy: 'Current User',
        data: taskToDelete
      });
      
      // Notify content change
      notifyContentChange('Tasks Page', 'deleted', 'Task', taskToDelete.title);
      
      alert('Task successfully moved to recycle bin!');
    } catch (error) {
      console.error('❌ Error during task deletion:', error);
      alert('An error occurred while deleting the task. Please try again.');
    }
  };

  const handleEditTask = (task: Task) => {
    // Populate the form with the task data
    setFormData({
      title: task.title,
      description: task.description,
      assignedTo: task.assignedTo,
      dueDate: task.dueDate,
      priority: task.priority,
      category: task.category
    });
    
    // Store the task ID for editing
    setEditingTaskId(task.id || null);
    setShowAddModal(true);
  };

  const taskStats = {
    total: tasks.length,
    pending: tasks.filter(t => t.status === 'PENDING').length,
    inProgress: tasks.filter(t => t.status === 'IN_PROGRESS').length,
    completed: tasks.filter(t => t.status === 'COMPLETED').length,
    overdue: tasks.filter(t => t.status === 'OVERDUE').length
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`${themeClasses.pageTitle} ${recruitmentTheme.animations.fadeIn}`}>Tasks</h1>
          <p className="text-gray-600 mt-1">
            Manage and track all tasks across your recruitment process
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button 
            onClick={() => setShowAutoDeletionSettings(true)}
            className={`${recruitmentTheme.components.button.secondary} flex items-center space-x-2`}
            title="Auto-deletion settings"
          >
            <Settings className="w-4 h-4" />
            <span>Auto-Delete</span>
            {autoDeletionEnabled && (
              <div className="w-2 h-2 bg-green-500 rounded-full" title="Auto-deletion enabled" />
            )}
          </button>
        </div>
      </div>

      {/* Task Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className={`${recruitmentTheme.components.card} ${recruitmentTheme.animations.scaleIn}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total</p>
              <p className="text-2xl font-bold text-[#8b6914]">{taskStats.total}</p>
            </div>
            <CheckSquare className="w-8 h-8 text-[#8b6914]" />
          </div>
        </div>

        <div className={`${recruitmentTheme.components.card} ${recruitmentTheme.animations.scaleIn}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-yellow-600">{taskStats.pending}</p>
            </div>
            <Target className="w-8 h-8 text-yellow-600" />
          </div>
        </div>

        <div className={`${recruitmentTheme.components.card} ${recruitmentTheme.animations.scaleIn}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">In Progress</p>
              <p className="text-2xl font-bold text-blue-600">{taskStats.inProgress}</p>
            </div>
            <Clock className="w-8 h-8 text-blue-600" />
          </div>
        </div>

        <div className={`${recruitmentTheme.components.card} ${recruitmentTheme.animations.scaleIn}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Completed</p>
              <p className="text-2xl font-bold text-green-600">{taskStats.completed}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
        </div>

        <div className={`${recruitmentTheme.components.card} ${recruitmentTheme.animations.scaleIn}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Overdue</p>
              <p className="text-2xl font-bold text-red-600">{taskStats.overdue}</p>
            </div>
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className={recruitmentTheme.components.card}>
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <CheckSquare className="w-5 h-5 text-[#8b6914]" />
            <h2 className={themeClasses.sectionTitle}>All Tasks</h2>
            <button 
              onClick={() => setShowAddModal(true)}
              className={`${recruitmentTheme.components.button.secondary} flex items-center space-x-2 ml-4`}
              data-add="true"
            >
              <Plus className="w-4 h-4" />
              <span>Add Task</span>
            </button>
            <div className="relative ml-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search tasks, assignees, categories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`${recruitmentTheme.components.input} pl-10 w-80`}
                data-search="true"
              />
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={recruitmentTheme.components.select}
            >
              <option value="all">All Status</option>
              {statusOptions.map(status => (
                <option key={status} value={status}>{status.replace('_', ' ')}</option>
              ))}
            </select>
            
            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className={recruitmentTheme.components.select}
            >
              <option value="all">All Priority</option>
              {localPriorityOptions.map(priority => (
                <option key={priority} value={priority}>{priority}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedTasks.length > 0 && (
          <div className="flex items-center justify-between mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <span className="text-sm text-blue-700">
              {selectedTasks.length} task(s) selected
            </span>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleDeleteSelected}
                className={`${recruitmentTheme.components.button.secondary} text-red-600 hover:text-red-700 flex items-center space-x-1`}
              >
                <Trash2 className="w-4 h-4" />
                <span>Delete Selected</span>
              </button>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mb-4">
          <label className="flex items-center space-x-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={selectedTasks.length === filteredTasks.length && filteredTasks.length > 0}
              onChange={(e) => handleSelectAll(e.target.checked)}
              className="rounded"
            />
            <span>Select All</span>
          </label>
          
          <button
            onClick={handleArchiveCompleted}
            className={`${recruitmentTheme.components.button.secondary} flex items-center space-x-1 text-sm`}
          >
            <Archive className="w-4 h-4" />
            <span>Archive Completed</span>
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                {columnOrder.map((columnId) => {
                  const config = columnConfig[columnId as keyof typeof columnConfig];
                  return (
                    <th 
                      key={columnId}
                      className={`text-left py-3 px-4 font-medium text-[#8b6914] ${config.width} ${
                        columnId !== 'select' && columnId !== 'status' ? 'cursor-move' : ''
                      }`}
                      draggable={columnId !== 'select' && columnId !== 'status'}
                      onDragStart={(e) => handleDragStart(e, columnId)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, columnId)}
                      style={{
                        backgroundColor: draggedColumn === columnId ? '#f3f4f6' : undefined
                      }}
                    >
                      <div className="flex items-center space-x-1">
                        {columnId !== 'select' && columnId !== 'status' && (
                          <GripVertical className="w-3 h-3 text-gray-400" />
                        )}
                        {columnId === 'select' ? (
                          <input
                            type="checkbox"
                            checked={selectedTasks.length === filteredTasks.length && filteredTasks.length > 0}
                            onChange={(e) => handleSelectAll(e.target.checked)}
                            className="rounded"
                          />
                        ) : config.sortable ? (
                          <button 
                            onClick={() => handleSort(config.sortField!)}
                            className="flex items-center space-x-1 hover:text-[#b8860b] transition-colors"
                          >
                            <span>{config.title}</span>
                            <ArrowUpDown className="w-3 h-3" />
                          </button>
                        ) : (
                          <span>{config.title}</span>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {filteredTasks.map((task, index) => (
                <tr key={task.id} className="border-b border-gray-100 hover:bg-gray-50">
                  {columnOrder.map((columnId) => (
                    <td key={columnId} className="py-3 px-4">
                      {columnId === 'task' ? (
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-2 flex-1">
                            <span className="font-bold text-[#8b6914] bg-[#f0e4cc] px-2 py-1 rounded text-sm min-w-[2rem] text-center">
                              {index + 1}.
                            </span>
                            <div className="flex-1">
                              <div className="font-medium text-[#8b6914]">{task.title}</div>
                              <div className="text-sm text-gray-500">{task.description}</div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-1 ml-2">
                            <button
                              onClick={() => handleEditTask(task)}
                              className={`${recruitmentTheme.components.button.ghost} text-blue-600 hover:text-blue-700 p-1`}
                              title="Edit task"
                            >
                              <Edit className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => task.id && handleDeleteTask(task.id)}
                              className={`${recruitmentTheme.components.button.ghost} text-red-600 hover:text-red-700 p-1`}
                              title="Delete task"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        renderTaskCell(task, columnId)
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredTasks.length === 0 && (
          <div className="text-center py-12">
            <CheckSquare className="mx-auto h-12 w-12 text-[#8b6914] opacity-50" />
            <h3 className="mt-2 text-sm font-medium text-[#8b6914]">No tasks found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || statusFilter !== 'all' || priorityFilter !== 'all' 
                ? 'Try adjusting your search or filter criteria.'
                : 'Get started by adding your first task.'
              }
            </p>
          </div>
        )}
      </div>

      {/* Add Task Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-[#8b6914]">Add New Task</h2>
              <button 
                onClick={() => { setShowAddModal(false); resetForm(); }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Task Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className={recruitmentTheme.components.input}
                  required
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className={`${recruitmentTheme.components.textarea} h-20`}
                  placeholder="Task details and instructions..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Assigned To *</label>
                <input
                  type="text"
                  value={formData.assignedTo}
                  onChange={(e) => setFormData({...formData, assignedTo: e.target.value})}
                  className={recruitmentTheme.components.input}
                  placeholder="Enter assignee name"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Due Date *</label>
                <input
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) => setFormData({...formData, dueDate: e.target.value})}
                  className={recruitmentTheme.components.input}
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Priority</label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData({...formData, priority: e.target.value})}
                  className={recruitmentTheme.components.select}
                >
                  {localPriorityOptions.map(priority => (
                    <option key={priority} value={priority}>{priority}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  className={recruitmentTheme.components.select}
                >
                  <option value="">Select category</option>
                  {categoryOptions.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button 
                onClick={() => { setShowAddModal(false); resetForm(); }}
                className={recruitmentTheme.components.button.secondary}
              >
                Cancel
              </button>
              <button 
                onClick={handleAddTask}
                className={`${recruitmentTheme.components.button.primary} flex items-center space-x-2`}
                disabled={!formData.title || !formData.assignedTo || !formData.dueDate}
              >
                <Save className="w-4 h-4" />
                <span>Add Task</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Auto-Deletion Settings Modal */}
      {showAutoDeletionSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-[#8b6914]">Task Auto-Deletion Settings</h2>
              <button 
                onClick={() => setShowAutoDeletionSettings(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={autoDeletionEnabled}
                  onChange={handleAutoDeletionToggle}
                  className="rounded"
                />
                <label className="text-sm font-medium text-[#8b6914]">
                  Enable auto-deletion for completed tasks
                </label>
              </div>
              
              {autoDeletionEnabled && (
                <div>
                  <label className="block text-sm font-medium text-[#8b6914] mb-1">
                    Delete after (days):
                  </label>
                  <select
                    value={autoDeletionDays}
                    onChange={(e) => setAutoDeletionDays(parseInt(e.target.value))}
                    className={recruitmentTheme.components.select}
                  >
                    <option value={7}>7 days</option>
                    <option value={14}>14 days (2 weeks)</option>
                    <option value={30}>30 days (1 month)</option>
                    <option value={60}>60 days (2 months)</option>
                    <option value={90}>90 days (3 months)</option>
                  </select>
                </div>
              )}
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <div className="flex items-start space-x-2">
                  <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-yellow-800">Auto-Deletion Policy</p>
                    <p className="text-xs text-yellow-700 mt-1">
                      Only tasks with "COMPLETED" status will be auto-deleted. 
                      This helps maintain active task tracking while cleaning up finished work.
                    </p>
                  </div>
                </div>
              </div>
              
              {autoDeletionEnabled && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-center space-x-2 mb-2">
                    <Clock className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium text-blue-800">Current Status</span>
                  </div>
                  <p className="text-xs text-blue-700">
                    {getCompletedTasksForDeletion().length} task(s) eligible for deletion
                  </p>
                  {getCompletedTasksForDeletion().length > 0 && (
                    <button
                      onClick={executeTaskAutoDeletion}
                      className="mt-2 text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 transition-colors"
                    >
                      Delete Now
                    </button>
                  )}
                </div>
              )}
              
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                <div className="flex items-center space-x-2 mb-2">
                  <Trash2 className="w-4 h-4 text-orange-600" />
                  <span className="text-sm font-medium text-orange-800">Manual Deletion</span>
                </div>
                <p className="text-xs text-orange-700 mb-2">
                  Delete all completed tasks instantly, regardless of completion date
                </p>
                <button
                  onClick={async () => {
                    const completedTasks = tasks.filter(task => task.status === 'COMPLETED');
                    if (completedTasks.length === 0) {
                      alert('No completed tasks to delete.');
                      return;
                    }
                    if (window.confirm(`Manually delete all ${completedTasks.length} completed task(s) immediately?`)) {
                      // Delete each completed task from Firebase
                      let deletedCount = 0;
                      const deletePromises = completedTasks.map(async (task) => {
                        try {
                          await deleteTaskFromFirebase(task.id || '');
                          deletedCount++;
                        } catch (error) {
                          console.error('❌ Failed to delete completed task:', error);
                        }
                      });
                      
                      await Promise.all(deletePromises);
                      alert(`${deletedCount} completed task(s) deleted!`);
                    }
                  }}
                  className="text-xs bg-orange-600 text-white px-3 py-1 rounded hover:bg-orange-700 transition-colors"
                >
                  Delete All Completed
                </button>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button 
                onClick={() => setShowAutoDeletionSettings(false)}
                className={recruitmentTheme.components.button.secondary}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TasksPage;